// Source of truth for the marketplace's filter chips, used by:
//   - CategoryStrip   (horizontal scroller on the homepage)
//   - FilterModal     (desktop "Toate filtrele" sheet)
//   - WhereModal      ("Serviciu" panel on the mobile search modal)
//
// Each chip carries `marketplaceTagSlug` (Phase 1 taxonomy, URL
// `?marketplaceTag=`). The legacy `?category=ENUM` reader was retired
// in Phase 7 alongside the ServiceCategory enum.
//
// Each entry maps a chip to one of three URL shapes:
//   - { marketplaceTagSlug } — Petzy-curated tag for service taxonomy.
//                              Writer emits `?marketplaceTag=slug`.
//   - { species:  'CAT' }    — Salon.supportedSpecies filter, written
//                              to ?species=.
//   - {} (Toate)             — clears every filter.
//
// 1:1 with the seeded MarketplaceTag rows (minus the catch-all that
// was intentionally never seeded). Order is the conversion funnel —
// high-intent / broad services first ("Toaletaj complet", "Tuns &
// Trimming", "Baie & Periere"), then audience pivots (species +
// life-stage), then niche or single-purpose entries. The strip becomes
// a soft reading path for indecisive visitors.
//
// Labels are inline RO strings here. Earlier revisions pulled them
// from a separate `SERVICE_CATEGORY_LABELS` constant; that source of
// truth retired in Phase 7 along with the ServiceCategory enum.
// The actively curated set is small (11 + 2 specials), so inlining
// is the right tradeoff — admin-added tags can pick up labels at
// runtime from `/marketplace-tags` if/when they get added to chips.

import {
  LuLayoutGrid,
  LuAward,
  LuScissors,
  LuBath,
  LuCat,
  LuHeart,
  LuBrush,
  LuFlower2,
  LuShield,
  LuPalette,
  LuPawPrint,
  LuEar,
  LuSparkles,
} from 'react-icons/lu';

// IDs are kebab-cased English mirrors of each chip's marketplace tag
// slug (with a couple of exceptions for the species axis + "Toate").
// They're INTERNAL — never serialised to the URL (the strip writes
// `?marketplaceTag=slug` for tag entries and `?species=CAT` for the
// species axis), never to the DB, never to the API. English-only
// matches the rest of the codebase (cf. AdminContentPage's
// `register-salon-info` / `cancel-policy`) and CLAUDE.md's RO/EN split:
// Romanian for user-facing strings (the `label` field is RO), English
// for identifiers.
//
// Adding them to a new constants file just for two strings would be
// churn without payoff when no
// i18n target is on the roadmap.
export const CATEGORIES = [
  { id: 'all',              label: 'Toate',                Icon: LuLayoutGrid                                                              },
  { id: 'full-grooming',    label: 'Toaletaj complet',     Icon: LuAward,    marketplaceTagSlug: 'toaletaj-complet'    },
  { id: 'haircut-trim',     label: 'Tuns & Trimming',      Icon: LuScissors, marketplaceTagSlug: 'tuns-trimming'       },
  { id: 'bath-brush',       label: 'Baie & Periere',       Icon: LuBath,     marketplaceTagSlug: 'baie-periere'        },
  { id: 'cat-grooming',     label: 'Toaletaj Pisici',      Icon: LuCat,      species:  'CAT'                           },
  { id: 'puppy-first',      label: 'Prima toaletare pui',  Icon: LuHeart,    marketplaceTagSlug: 'prima-toaletare-pui' },
  { id: 'deshedding',       label: 'Deshedding',           Icon: LuBrush,    marketplaceTagSlug: 'deshedding'          },
  { id: 'spa-treatment',    label: 'Tratament SPA',        Icon: LuFlower2,  marketplaceTagSlug: 'tratament-spa'       },
  { id: 'flea-treatment',   label: 'Tratament anti-purici',Icon: LuShield,   marketplaceTagSlug: 'anti-purici'         },
  { id: 'creative-styling', label: 'Styling creativ',      Icon: LuPalette,  marketplaceTagSlug: 'styling-creativ'     },
  { id: 'nail-trim',        label: 'Tăiat unghii',         Icon: LuPawPrint, marketplaceTagSlug: 'taiat-unghii'        },
  { id: 'ear-cleaning',     label: 'Curățat urechi',       Icon: LuEar,      marketplaceTagSlug: 'curatare-urechi'     },
  { id: 'teeth-cleaning',   label: 'Curățat dinți',        Icon: LuSparkles, marketplaceTagSlug: 'curatare-dinti'      },
];

// Resolve the currently-active chip id from the live URL params.
// "Toate" is the implicit answer when no filter is set.
//
// Species check is non-exclusive: a stale bookmark with
// ?species=invalid&marketplaceTag=baie-periere should still highlight
// the Baie & Periere chip rather than silently giving up at the
// species miss.
export function getActiveCategoryId(searchParams) {
  const urlTagSlug = searchParams.get('marketplaceTag') || '';
  const urlSpecies = searchParams.get('species') || '';
  if (!urlTagSlug && !urlSpecies) return 'all';
  if (urlSpecies) {
    const speciesMatch = CATEGORIES.find((c) => c.species === urlSpecies);
    if (speciesMatch) return speciesMatch.id;
  }
  if (urlTagSlug) {
    const slugMatch = CATEGORIES.find((c) => c.marketplaceTagSlug === urlTagSlug);
    if (slugMatch) return slugMatch.id;
  }
  return 'all';
}

// Apply a chip selection on top of `params` and return a NEW URLSearchParams
// — the input is never mutated, so callers can hand in React Router's memoized
// `prev` directly without cloning first. Always resets `marketplaceTag` +
// `species` first so jumping from Pisici → Spa doesn't leave a stale
// species=CAT behind. Also strips any legacy `?category=` param that might
// still appear in inbound traffic from external links written before the
// Phase 6 cutover. Pass null (or the "all" entry) to clear everything.
export function commitCategoryToUrl(params, entry) {
  const next = new URLSearchParams(params);
  next.delete('marketplaceTag');
  next.delete('category');
  next.delete('species');
  if (entry?.marketplaceTagSlug) next.set('marketplaceTag', entry.marketplaceTagSlug);
  if (entry?.species) next.set('species', entry.species);
  next.delete('page');
  return next;
}

// The full set of marketplace filter params a "clear all" must drop, in one
// place so the surfaces that clear filters (WhereModal's clearAll, HomePage's
// clearFilters) can't drift — they previously each missed a different one
// (clearAll left the active `marketplaceTag`, clearFilters left the legacy
// `category`). `category` is kept here for old inbound ?category= links,
// mirroring commitCategoryToUrl. Returns a new URLSearchParams; never mutates
// the input.
export function clearFilterParams(params) {
  const next = new URLSearchParams(params);
  for (const key of ['q', 'city', 'marketplaceTag', 'category', 'species', 'page']) {
    next.delete(key);
  }
  return next;
}
